import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { AdminStatsPayload, StatsRange } from "./apiTypes";
import type { SurveyFieldDef } from "../survey/config";
import { buildFieldMap, formatFieldValue } from "./formatAnswers";
import { MiniBarTable, SemaphoreList } from "./ui";

const SOM_DEFAULT = 19298;
const ADOPTIONS = [1, 3, 5] as const;
const PRICES = [5, 10, 20] as const;

function segLabel(s: string) {
  if (s === "na") return "N/A";
  return s;
}

function distRows(
  items: { key: string; count: number }[],
  fieldId: string,
  fm: Map<string, SurveyFieldDef>,
) {
  return items.map((x) => ({
    key: formatFieldValue(fm.get(fieldId), x.key),
    count: x.count,
  }));
}

function readoutDot(level: "green" | "yellow" | "red") {
  const c =
    level === "green"
      ? "bg-emerald-500"
      : level === "yellow"
        ? "bg-amber-400"
        : "bg-red-500";
  return <span className={`inline-block w-3 h-3 rounded-full ${c}`} aria-hidden />;
}

function FounderCard({
  label,
  value,
  foot,
}: {
  label: string;
  value: string | number;
  foot: string;
}) {
  return (
    <div className="rounded-xl border border-pp-border bg-gradient-to-b from-pp-card to-pp-soft/90 p-4 shadow-sm ring-1 ring-pp-border">
      <p className="text-[10px] uppercase tracking-wider text-pp-quiet font-medium leading-tight">
        {label}
      </p>
      <p className="text-3xl sm:text-4xl font-semibold tabular-nums tracking-tight text-pp-main mt-1.5">
        {value}
      </p>
      <p className="text-[11px] text-pp-muted mt-2 font-mono leading-snug">{foot}</p>
    </div>
  );
}

const DIM_FIELD: Record<string, string> = {
  q1: "q1_friction_freq",
  q3: "q3_consequence",
  q6: "q6_what_breaks",
  q10: "q10_objections",
};

const DIM_LABEL: Record<string, string> = {
  q1: "Top q1",
  q3: "Top q3",
  q6: "Top q6",
  q10: "Top q10",
};

export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get("survey_id") ?? "";
  const [range, setRange] = useState<StatsRange>("1m");
  const [stats, setStats] = useState<AdminStatsPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [somBase, setSomBase] = useState(SOM_DEFAULT);

  const fm = useMemo(() => buildFieldMap(), []);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const q = new URLSearchParams({ range });
      if (surveyId) q.set("survey_id", surveyId);
      const res = await fetch(`/api/admin/stats?${q.toString()}`, {
        credentials: "include",
      });
      if (res.status === 401) return;
      if (!res.ok) {
        setErr("No se pudieron cargar datos.");
        return;
      }
      setStats((await res.json()) as AdminStatsPayload);
    } catch {
      setErr("Error de red.");
    }
  }, [range, surveyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!stats) {
    return <p className="text-sm text-pp-muted">{err ?? "Cargando…"}</p>;
  }

  const bins = stats.score_bins;
  const mxBin = Math.max(1, ...bins.map((b) => b.count));
  const segRows = Object.entries(stats.segments)
    .sort((a, b) => b[1] - a[1])
    .map(([k, c]) => ({ key: segLabel(k), count: c }));
  const profRows = [
    { key: "P1", count: stats.totals.total_p1 },
    { key: "P2", count: stats.totals.total_p2 },
    { key: "P2n", count: stats.totals.total_p2n },
    { key: "P3", count: stats.totals.total_p3 },
  ].filter((x) => x.count > 0);
  const sigRows = Object.entries(stats.signals)
    .sort((a, b) => b[1] - a[1])
    .map(([k, c]) => ({ key: k, count: c }));

  const mxFunnel = 100;
  const pf = stats.price_fit;
  const mxPriceFit = Math.max(pf.pct_fit_5, pf.pct_fit_10, pf.pct_fit_20, 1);
  const qc = stats.quadrant_counts;
  const mxQ = Math.max(1, qc.priority + qc.painful_hard_adoption + qc.medium_opportunity + qc.low_focus);

  const p2n = stats.p2_count;

  return (
    <div className="space-y-8">
      {err && <p className="text-sm text-red-700">{err}</p>}

      <details className="group border border-pp-border rounded-lg bg-pp-card open:shadow-sm">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-pp-main list-none flex items-center gap-2 [&::-webkit-details-marker]:hidden">
          <span className="text-pp-quiet group-open:rotate-90 transition-transform inline-block">
            ▸
          </span>
          Leyenda
        </summary>
        <ul className="px-3 pb-3 pt-0 text-xs text-pp-muted space-y-1.5 border-t border-pp-border mt-0 pt-3 font-mono leading-relaxed">
          <li>P1: un hogar (control / fuera de foco)</li>
          <li>P2: dos hogares (mercado objetivo)</li>
          <li>P3: profesional (prescriptor)</li>
          <li>Score (0–100): dolor + urgencia + señales − objeciones</li>
          <li>Segmento: bajo &lt;40 · medio 40–69 · alto ≥70</li>
          <li>Señal: hot = priorizar contacto · warm = investigar · cold = baja prioridad</li>
          <li>Compromiso: “sí/tal vez” o dejó email (evidencia Mom Test)</li>
        </ul>
      </details>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          className="inline-flex rounded-lg border border-pp-border p-0.5 bg-pp-soft"
          role="group"
          aria-label="Ventana de tiempo"
        >
          {(
            [
              ["1d", "1d"],
              ["1w", "1w"],
              ["1m", "1m"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setRange(v)}
              className={`px-3 py-1.5 text-xs font-mono rounded-md transition-colors ${
                range === v
                  ? "bg-pp-gradient text-white"
                  : "text-pp-muted hover:bg-pp-soft/80"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-mono">
          <Link
            to={`/admin/responses?${new URLSearchParams({
              ...(surveyId ? { survey_id: surveyId } : {}),
              signal: "hot",
            }).toString()}`}
            className="px-2 py-1 rounded border border-pp-border hover:bg-pp-soft"
          >
            HOT →
          </Link>
          <Link
            to={`/admin/responses?${new URLSearchParams({
              ...(surveyId ? { survey_id: surveyId } : {}),
              profile: "p2",
            }).toString()}`}
            className="px-2 py-1 rounded border border-pp-border hover:bg-pp-soft"
          >
            P2 →
          </Link>
          <Link
            to={`/admin/responses?${new URLSearchParams({
              ...(surveyId ? { survey_id: surveyId } : {}),
              has_email: "1",
            }).toString()}`}
            className="px-2 py-1 rounded border border-pp-border hover:bg-pp-soft"
          >
            con email →
          </Link>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border rounded-lg p-4 bg-pp-card">
          <div className="flex items-center gap-2 mb-2">
            {readoutDot(stats.mom_test_readout.level)}
            <p className="text-sm font-medium text-pp-main">Mom Test readout</p>
          </div>
          <p className="text-sm text-pp-muted mb-3">{stats.mom_test_readout.summary}</p>
          <ul className="text-xs font-mono text-pp-muted space-y-1">
            {stats.mom_test_readout.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
        <div className="border rounded-lg p-4 bg-pp-card">
          <p className="text-sm font-medium text-pp-main mb-2">Next move</p>
          <p className="text-sm text-pp-main leading-relaxed">{stats.next_move}</p>
        </div>
      </div>

      <div>
        <p className="text-[11px] text-pp-quiet font-mono mb-2">
          KPI foco P2+P2n · denominador para % = {p2n} respuestas P2+P2n
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <FounderCard
            label="P2 responses"
            value={p2n}
            foot={`P2+P2n en ventana · ${stats.range}`}
          />
          <FounderCard
            label="HOT"
            value={stats.hot_count_p2}
            foot={`${stats.hot_pct_of_p2}% de P2+P2n`}
          />
          <FounderCard
            label="Segmento alto"
            value={stats.high_segment_count_p2}
            foot={`${stats.high_segment_pct_of_p2}% de P2+P2n`}
          />
          <FounderCard
            label="Email opt-in"
            value={stats.email_count_p2}
            foot={`${stats.email_pct_of_p2}% de P2+P2n`}
          />
        </div>
        <p className="text-xs text-pp-quiet mt-2">
          Total muestra (todos los perfiles):{" "}
          <span className="font-mono text-pp-muted">{stats.totals.total_responses}</span>
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <section>
          <p className="text-sm font-medium text-pp-muted mb-1">
            Matriz 2×2 · Prioridad (P2+P2n)
          </p>
          <p className="text-[11px] text-pp-quiet font-mono mb-2">
            score_total + score_penalty · celdas = conteo de respuestas
          </p>
          <div className="grid grid-cols-2 gap-2 max-w-md text-xs">
            <div className="border rounded-lg p-3 bg-emerald-50/40 border-emerald-200/60">
              <p className="font-medium text-pp-main">Priority</p>
              <p className="text-[10px] text-pp-muted mt-0.5">score≥70 &amp; penalty&gt;-6</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{qc.priority}</p>
              <div className="mt-2 h-1.5 bg-emerald-200/50 rounded overflow-hidden">
                <div
                  className="h-full bg-emerald-600 rounded"
                  style={{ width: `${(qc.priority / mxQ) * 100}%` }}
                />
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-amber-50/40 border-amber-200/60">
              <p className="font-medium text-pp-main">Painful / hard adoption</p>
              <p className="text-[10px] text-pp-muted mt-0.5">score≥70 &amp; penalty≤-6</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{qc.painful_hard_adoption}</p>
              <div className="mt-2 h-1.5 bg-amber-200/50 rounded overflow-hidden">
                <div
                  className="h-full bg-amber-600 rounded"
                  style={{ width: `${(qc.painful_hard_adoption / mxQ) * 100}%` }}
                />
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-sky-50/40 border-sky-200/60">
              <p className="font-medium text-pp-main">Medium opportunity</p>
              <p className="text-[10px] text-pp-muted mt-0.5">score 40–69 &amp; penalty&gt;-6</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{qc.medium_opportunity}</p>
              <div className="mt-2 h-1.5 bg-sky-200/50 rounded overflow-hidden">
                <div
                  className="h-full bg-sky-600 rounded"
                  style={{ width: `${(qc.medium_opportunity / mxQ) * 100}%` }}
                />
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-pp-soft/80 border-pp-border">
              <p className="font-medium text-pp-main">Low focus</p>
              <p className="text-[10px] text-pp-muted mt-0.5">resto</p>
              <p className="text-2xl font-semibold tabular-nums mt-2">{qc.low_focus}</p>
              <div className="mt-2 h-1.5 bg-pp-soft rounded overflow-hidden">
                <div
                  className="h-full bg-pp-muted rounded"
                  style={{ width: `${(qc.low_focus / mxQ) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </section>

        <section>
          <p className="text-sm font-medium text-pp-muted mb-1">Top drivers</p>
          <p className="text-[11px] text-pp-quiet font-mono mb-2">
            % = respuestas P2+P2n con ese valor (al menos una mención en multi)
          </p>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-xs text-left">
              <thead className="bg-pp-soft border-b border-pp-border">
                <tr>
                  <th className="p-2 font-medium">#</th>
                  <th className="p-2 font-medium">Driver</th>
                  <th className="p-2 font-medium">Valor</th>
                  <th className="p-2 font-medium">n</th>
                  <th className="p-2 font-medium">% P2</th>
                  <th className="p-2 font-medium min-w-[140px]">So what</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_drivers.map((row) => (
                  <tr key={row.rank} className="border-t border-pp-border">
                    <td className="p-2 font-mono tabular-nums">{row.rank}</td>
                    <td className="p-2 font-mono text-pp-muted">
                      {DIM_LABEL[row.dimension] ?? row.dimension}
                    </td>
                    <td className="p-2">
                      {row.key
                        ? formatFieldValue(fm.get(DIM_FIELD[row.dimension]), row.key)
                        : "—"}
                    </td>
                    <td className="p-2 font-mono tabular-nums">{row.count}</td>
                    <td className="p-2 font-mono tabular-nums">{row.share_pct_of_p2}%</td>
                    <td className="p-2 text-pp-main leading-snug">{row.so_what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
          <p className="text-sm font-medium text-pp-muted">
            Score bins (excl. P1) · <span className="font-mono">{stats.range}</span>
          </p>
          <p className="text-xs font-mono text-pp-muted">
            Avg P2: <span className="text-pp-main font-medium">{stats.avg_score_p2}</span>
            {" · "}
            P90 P2: <span className="text-pp-main font-medium">{stats.p90_score_p2}</span>
          </p>
        </div>
        <p className="text-[11px] text-pp-quiet font-mono mb-2">
          Histograma: conteos por bin sobre respuestas con score (excl. P1), no solo P2
        </p>
        <ul className="space-y-2 max-w-lg">
          {bins.map((b) => (
            <li key={b.label} className="flex items-center gap-3 text-sm">
              <span className="w-20 font-mono text-xs text-pp-muted shrink-0">{b.label}</span>
              <div className="flex-1 h-3 bg-pp-soft rounded overflow-hidden min-w-0">
                <div
                  className="h-full rounded bg-pp-gradient"
                  style={{ width: `${Math.round((b.count / mxBin) * 100)}%` }}
                />
              </div>
              <span className="w-8 text-right font-mono text-xs tabular-nums text-pp-muted">
                {b.count}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <p className="text-sm font-medium text-pp-muted mb-1">Funnel P2+P2n</p>
        <p className="text-[11px] text-pp-quiet font-mono mb-2">
          Base = {stats.funnel_p2.base} · % en cada paso sobre P2+P2n
        </p>
        <ul className="space-y-2 max-w-lg">
          {stats.funnel_p2.steps.map((s) => (
            <li key={s.id} className="flex items-center gap-3 text-sm">
              <span className="flex-1 min-w-0 text-pp-main">{s.label}</span>
              <span className="font-mono text-xs text-pp-quiet w-12 text-right">
                {s.pct_of_p2}%
              </span>
              <div className="w-32 h-2.5 bg-pp-soft rounded overflow-hidden shrink-0">
                <div
                  className="h-full bg-pp-muted rounded"
                  style={{
                    width: `${Math.round((s.pct_of_p2 / mxFunnel) * 100)}%`,
                  }}
                />
              </div>
              <span className="w-8 font-mono text-xs tabular-nums text-right">{s.count}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border rounded-lg p-4 bg-pp-soft/50 space-y-4">
        <p className="text-sm font-medium text-pp-main">Dinero</p>
        <div>
          <p className="text-xs text-pp-muted mb-2 font-mono leading-relaxed">
            Price fit (q9 “caro”) · denominador = {pf.denom_p2_q9} P2+P2n con q9 distinta de vacío /
            prefer_not · % = share sobre ese n
          </p>
          <ul className="space-y-2 max-w-md">
            <li className="flex items-center gap-2 text-sm">
              <span className="w-24 font-mono text-xs">Fit $5</span>
              <div className="flex-1 h-2.5 bg-pp-soft rounded overflow-hidden">
                <div
                  className="h-full bg-pp-main rounded"
                  style={{ width: `${(pf.pct_fit_5 / mxPriceFit) * 100}%` }}
                />
              </div>
              <span className="w-14 text-right font-mono text-xs">{pf.pct_fit_5}%</span>
            </li>
            <li className="flex items-center gap-2 text-sm">
              <span className="w-24 font-mono text-xs">Fit $10</span>
              <div className="flex-1 h-2.5 bg-pp-soft rounded overflow-hidden">
                <div
                  className="h-full bg-pp-main rounded"
                  style={{ width: `${(pf.pct_fit_10 / mxPriceFit) * 100}%` }}
                />
              </div>
              <span className="w-14 text-right font-mono text-xs">{pf.pct_fit_10}%</span>
            </li>
            <li className="flex items-center gap-2 text-sm">
              <span className="w-24 font-mono text-xs">Fit $20</span>
              <div className="flex-1 h-2.5 bg-pp-soft rounded overflow-hidden">
                <div
                  className="h-full bg-pp-main rounded"
                  style={{ width: `${(pf.pct_fit_20 / mxPriceFit) * 100}%` }}
                />
              </div>
              <span className="w-14 text-right font-mono text-xs">{pf.pct_fit_20}%</span>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium text-pp-muted mb-2">Revenue sandbox (no persistido)</p>
          <label className="flex flex-wrap items-center gap-2 text-xs font-mono mb-2">
            <span>SOM pagos (base editable)</span>
            <input
              type="number"
              min={0}
              value={somBase}
              onChange={(e) => setSomBase(Math.max(0, parseInt(e.target.value, 10) || 0))}
              className="w-28 border rounded px-2 py-1 text-pp-main"
            />
          </label>
          <p className="text-xs text-pp-quiet mb-2 font-mono">
            MRR = SOM × adopción × $/mes · ARR = MRR × 12
          </p>
          <div className="overflow-x-auto">
            <table className="text-xs font-mono border-collapse w-full max-w-xl">
              <thead>
                <tr className="border-b border-pp-border">
                  <th className="text-left py-1 pr-2">adopción \ $/mes</th>
                  {PRICES.map((p) => (
                    <th key={p} className="text-right py-1 px-1">
                      ${p}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ADOPTIONS.map((a) => (
                  <tr key={a} className="border-b border-pp-border">
                    <td className="py-1 pr-2 text-pp-muted">{a}%</td>
                    {PRICES.map((price) => {
                      const mrr = Math.round(somBase * (a / 100) * price);
                      const arr = mrr * 12;
                      return (
                        <td key={price} className="text-right py-1 px-1 text-pp-main align-top">
                          <div>MRR {mrr.toLocaleString()}</div>
                          <div className="text-pp-quiet">ARR {(arr / 1000).toFixed(0)}k</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="text-sm font-medium mb-2">Perfil (conteo)</p>
          <p className="text-[10px] text-pp-quiet font-mono mb-1">% barras relativas al total muestra</p>
          <MiniBarTable rows={profRows} />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Segmento</p>
          <p className="text-[10px] text-pp-quiet font-mono mb-1">total muestra</p>
          <MiniBarTable rows={segRows} />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Señal</p>
          <p className="text-[10px] text-pp-quiet font-mono mb-1">total muestra</p>
          <MiniBarTable rows={sigRows} />
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium mb-2">Fricción (q1), P2</p>
          <p className="text-[10px] text-pp-quiet font-mono mb-1">distribución menciones · ver % P2 en Top drivers</p>
          <MiniBarTable rows={distRows(stats.distributions.q1_friction_freq, "q1_friction_freq", fm)} />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Tiempo semanal (q2), P2</p>
          <MiniBarTable rows={distRows(stats.distributions.q2_time_week, "q2_time_week", fm)} />
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium mb-2">Dolor — consecuencias (q3), P2</p>
          <MiniBarTable rows={distRows(stats.distributions.q3_consequence, "q3_consequence", fm)} />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Qué falla (q6), P2</p>
          <MiniBarTable rows={distRows(stats.distributions.q6_what_breaks, "q6_what_breaks", fm)} />
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium mb-2">Objeciones (q10), P2</p>
          <MiniBarTable rows={distRows(stats.distributions.q10_objections, "q10_objections", fm)} />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Pago proxy — gasto (q8)</p>
          <MiniBarTable rows={distRows(stats.distributions.q8_spend_now, "q8_spend_now", fm)} />
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium mb-2">Sensibilidad precio (q9)</p>
          <MiniBarTable rows={distRows(stats.distributions.q9_price_sense, "q9_price_sense", fm)} />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Compromiso P2 (q11)</p>
          <MiniBarTable rows={distRows(stats.distributions.q11_commitment, "q11_commitment", fm)} />
        </div>
      </section>

      <section>
        <p className="text-sm font-medium mb-2">Compromiso P3 (p3q8)</p>
        <MiniBarTable
          rows={distRows(stats.distributions.p3q8_commitment, "p3q8_commitment", fm)}
        />
      </section>

      <section>
        <p className="text-sm font-medium mb-2">Insights automáticos</p>
        <SemaphoreList items={stats.insights} />
      </section>
    </div>
  );
}
